// Usuários e vínculos. As opções de acesso oferecidas são só as que o usuário logado pode
// conceder (derivadas das flags canManage da árvore); a API confere de novo.

import { useQuery } from "@tanstack/react-query";
import { Copy, KeyRound, Mail, MoreHorizontal, Plus, ShieldOff, Trash2, UserCheck, UserX } from "lucide-react";
import { type FormEvent, useMemo, useState } from "react";
import { Alert, Badge, Button, EmptyState, Field, Input, Modal, PageHeader, Select, Spinner, Table } from "../../components/ui";
import { type AdminUser, api, type MembershipRow, type Role, ROLE_LABEL, type Tree } from "../../lib/api";
import { treeIndex, useAction, useTree } from "../../lib/hooks";

export function UsersPage() {
  const { data: tree } = useTree();
  const { data, isLoading } = useQuery({ queryKey: ["users"], queryFn: () => api.get<{ users: AdminUser[] }>("/api/admin/users") });
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<AdminUser | null>(null);
  const [filter, setFilter] = useState("");
  const idx = useMemo(() => treeIndex(tree), [tree]);

  const users = (data?.users ?? []).filter(
    (u) => !filter || `${u.name} ${u.email}`.toLowerCase().includes(filter.toLowerCase()),
  );
  const current = selected ? data?.users.find((u) => u.id === selected.id) ?? null : null;

  return (
    <>
      <PageHeader
        title="Usuários"
        description="Quem acessa a plataforma e o que cada um pode ver ou configurar."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" /> Novo usuário
          </Button>
        }
      />
      <div className="mb-4 max-w-sm">
        <Input placeholder="Buscar por nome ou e-mail" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Buscar usuário" />
      </div>
      {isLoading ? (
        <Spinner />
      ) : users.length === 0 ? (
        <EmptyState title="Nenhum usuário" />
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Usuário</th>
              <th>Acessos</th>
              <th>Situação</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>
                  <p className="font-medium">{u.name}</p>
                  <p className="text-xs text-muted">{u.email}</p>
                </td>
                <td>
                  <div className="flex flex-col gap-1">
                    {u.memberships.map((m) => (
                      <span key={m.id} className="text-xs">
                        <Badge tone="brand">{ROLE_LABEL[m.role]}</Badge> <span className="text-muted">{scopeLabel(m, idx)}</span>
                      </span>
                    ))}
                  </div>
                </td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {u.disabled && <Badge tone="danger">bloqueado</Badge>}
                    {u.pendingInvite && <Badge tone="warn">convite pendente</Badge>}
                    {u.temporaryPassword && <Badge tone="warn">senha temporária</Badge>}
                    {!u.disabled && !u.pendingInvite && !u.temporaryPassword && <Badge tone="ok">ativo</Badge>}
                    {u.totpEnabled && <Badge>2FA</Badge>}
                  </div>
                  <p className="mt-1 text-xs text-muted">
                    {u.lastLoginAt ? `último acesso ${new Date(u.lastLoginAt).toLocaleString("pt-BR")}` : "nunca acessou"}
                  </p>
                </td>
                <td>
                  {u.canManage && (
                    <button type="button" onClick={() => setSelected(u)} className="rounded p-1.5 text-muted hover:bg-surface-2 hover:text-fg" aria-label={`Gerenciar ${u.name}`}>
                      <MoreHorizontal className="size-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {creating && tree && <CreateUserDialog tree={tree} onClose={() => setCreating(false)} />}
      {current && tree && <ManageUserDialog user={current} tree={tree} onClose={() => setSelected(null)} />}
    </>
  );
}

type Index = ReturnType<typeof treeIndex>;

function scopeLabel(m: MembershipRow, idx: Index): string {
  if (m.role === "platform_admin") return "toda a plataforma";
  if (m.groupId) return idx.groups.get(m.groupId) ?? `grupo ${m.groupId}`;
  if (m.siteId) {
    const s = idx.sites.get(m.siteId);
    return s ? `${s.group} › ${s.name}` : `usina ${m.siteId}`;
  }
  if (m.unitId) {
    const u = idx.units.get(m.unitId);
    return u ? `${u.site} › ${u.name}` : `UG ${m.unitId}`;
  }
  return "";
}

// ── Escolha de papel + escopo ────────────────────────────────────────────────

interface Grant {
  role: Role;
  groupId?: number;
  siteId?: number;
  unitId?: number;
}

/** O que o usuário logado pode conceder, montado a partir da árvore. */
function grantOptions(tree: Tree) {
  const groups = tree.groups.filter((g) => g.canManage);
  const sites = tree.groups.flatMap((g) => g.sites.filter((s) => s.canManage).map((s) => ({ ...s, group: g.name })));
  const units = sites.flatMap((s) => s.units.map((u) => ({ ...u, site: s.name, group: s.group })));
  const roles: Role[] = [];
  if (tree.isAdmin) roles.push("platform_admin");
  if (groups.length) roles.push("group_manager");
  if (sites.length) roles.push("site_manager");
  if (units.length) roles.push("viewer");
  return { roles, groups, sites, units };
}

function GrantPicker({ tree, value, onChange }: { tree: Tree; value: Grant; onChange: (g: Grant) => void }) {
  const opt = useMemo(() => grantOptions(tree), [tree]);
  const scope =
    value.role === "group_manager"
      ? opt.groups.map((g) => ({ id: g.id, label: g.name }))
      : value.role === "site_manager"
        ? opt.sites.map((s) => ({ id: s.id, label: `${s.group} › ${s.name}` }))
        : value.role === "viewer"
          ? opt.units.map((u) => ({ id: u.id, label: `${u.site} › ${u.name}` }))
          : [];
  const key = value.role === "group_manager" ? "groupId" : value.role === "site_manager" ? "siteId" : "unitId";
  const selectedId = value[key as keyof Grant] as number | undefined;

  if (!opt.roles.length) return <Alert kind="error">Você não tem escopo para conceder acessos.</Alert>;

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label="Papel">
        {(id) => (
          <Select
            id={id}
            value={value.role}
            onChange={(e) => {
              const role = e.target.value as Role;
              const first =
                role === "group_manager" ? { groupId: opt.groups[0]?.id } : role === "site_manager" ? { siteId: opt.sites[0]?.id } : role === "viewer" ? { unitId: opt.units[0]?.id } : {};
              onChange({ role, ...first });
            }}
          >
            {opt.roles.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {value.role !== "platform_admin" && (
        <Field label={value.role === "group_manager" ? "Grupo" : value.role === "site_manager" ? "Usina" : "Unidade geradora"}>
          {(id) => (
            <Select id={id} value={selectedId ?? ""} onChange={(e) => onChange({ role: value.role, [key]: Number(e.target.value) })}>
              {scope.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}
    </div>
  );
}

function defaultGrant(tree: Tree): Grant {
  const opt = grantOptions(tree);
  if (opt.units.length) return { role: "viewer", unitId: opt.units[0]!.id };
  if (opt.sites.length) return { role: "site_manager", siteId: opt.sites[0]!.id };
  if (opt.groups.length) return { role: "group_manager", groupId: opt.groups[0]!.id };
  return { role: "platform_admin" };
}

const ROLE_HELP: Record<Role, string> = {
  platform_admin: "Vê e configura toda a plataforma, de todos os grupos.",
  group_manager: "Vê e configura todas as usinas do grupo, a identidade visual e os usuários do grupo.",
  site_manager: "Vê e configura a usina e seus usuários.",
  viewer: "Só visualiza a unidade geradora.",
};

// ── Criar ────────────────────────────────────────────────────────────────────

/**
 * A senha temporária aparece uma única vez (o servidor só guarda o hash): quem criou copia e
 * passa ao usuário por outro meio. No primeiro acesso a troca é obrigatória.
 */
function TemporaryPasswordBox({ password, email }: { password: string; email: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-3">
      <Alert kind="ok">
        Senha temporária de <strong>{email}</strong>:
      </Alert>
      <div className="flex items-center gap-2">
        <code className="flex-1 rounded-md border border-line bg-surface-2 px-3 py-2 text-center font-mono text-lg tracking-wider select-all">
          {password}
        </code>
        <Button
          variant="secondary"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(password);
              setCopied(true);
            } catch {
              setCopied(false);
            }
          }}
        >
          <Copy className="size-4" aria-hidden /> {copied ? "Copiada" : "Copiar"}
        </Button>
      </div>
      <p className="text-xs text-muted">
        Ela aparece só agora. Passe ao usuário por um meio seguro; no primeiro acesso ele é obrigado a trocá-la por uma senha própria.
      </p>
    </div>
  );
}

function CreateUserDialog({ tree, onClose }: { tree: Tree; onClose: () => void }) {
  const { run, busy, error } = useAction([["users"]]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [grant, setGrant] = useState<Grant>(() => defaultGrant(tree));
  // Sem SMTP o convite não chega: a senha temporária é o padrão.
  const [access, setAccess] = useState<"temporary" | "invite">("temporary");
  const [done, setDone] = useState(false);
  const [temporary, setTemporary] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    await run(async () => {
      const r = await api.post<{ id: number; temporaryPassword?: string }>("/api/admin/users", { name, email, memberships: [grant], access });
      setTemporary(r.temporaryPassword ?? null);
      setDone(true);
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Novo usuário"
      footer={
        done ? (
          <Button onClick={onClose}>Fechar</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" form="create-user" loading={busy}>
              {access === "temporary" ? "Criar com senha temporária" : "Criar e enviar convite"}
            </Button>
          </>
        )
      }
    >
      {done ? (
        temporary ? (
          <TemporaryPasswordBox password={temporary} email={email} />
        ) : (
          <Alert kind="ok">
            Usuário criado. O convite para definir a senha foi enviado para <strong>{email}</strong> e vale por 7 dias.
          </Alert>
        )
      ) : (
        <form id="create-user" onSubmit={submit} className="space-y-4">
          {error && <Alert kind="error">{error}</Alert>}
          <Field label="Nome">{(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} required autoFocus />}</Field>
          <Field label="E-mail">{(id) => <Input id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />}</Field>
          <GrantPicker tree={tree} value={grant} onChange={setGrant} />
          <p className="text-xs text-muted">{ROLE_HELP[grant.role]}</p>
          <fieldset className="space-y-2">
            <legend className="mb-1 text-sm font-medium">Primeiro acesso</legend>
            <label className="flex items-start gap-2 text-sm">
              <input type="radio" name="access" checked={access === "temporary"} onChange={() => setAccess("temporary")} className="mt-1 accent-[var(--brand)]" />
              <span>
                Senha temporária
                <span className="block text-xs text-muted">Gerada agora e mostrada a você uma vez; o usuário troca no primeiro acesso.</span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input type="radio" name="access" checked={access === "invite"} onChange={() => setAccess("invite")} className="mt-1 accent-[var(--brand)]" />
              <span>
                Convite por e-mail
                <span className="block text-xs text-muted">Link para o próprio usuário definir a senha. Exige o envio de e-mail configurado.</span>
              </span>
            </label>
          </fieldset>
        </form>
      )}
    </Modal>
  );
}

// ── Gerenciar ────────────────────────────────────────────────────────────────

function ManageUserDialog({ user, tree, onClose }: { user: AdminUser; tree: Tree; onClose: () => void }) {
  const { run, busy, error } = useAction([["users"]]);
  const idx = useMemo(() => treeIndex(tree), [tree]);
  const [name, setName] = useState(user.name);
  const [grant, setGrant] = useState<Grant>(() => defaultGrant(tree));
  const [notice, setNotice] = useState<string | null>(null);
  const [temporary, setTemporary] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setNotice(null);
    setTemporary(null);
    if (await run(fn)) setNotice(ok);
  };

  if (temporary) {
    return (
      <Modal open onClose={onClose} title={user.name} footer={<Button onClick={onClose}>Fechar</Button>}>
        <TemporaryPasswordBox password={temporary} email={user.email} />
        <p className="text-xs text-muted">As sessões abertas do usuário foram encerradas e a senha anterior deixou de valer.</p>
      </Modal>
    );
  }

  if (confirmDelete) {
    return (
      <Modal
        open
        onClose={() => setConfirmDelete(false)}
        title="Apagar usuário"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
              Cancelar
            </Button>
            <Button variant="danger" loading={busy} onClick={async () => (await run(() => api.del(`/api/admin/users/${user.id}`))) && onClose()}>
              <Trash2 className="size-4" aria-hidden /> Apagar
            </Button>
          </>
        }
      >
        {error && <Alert kind="error">{error}</Alert>}
        <p className="text-sm">
          Apagar <strong>{user.name}</strong> ({user.email}) de vez? Os acessos e as sessões dele somem. O histórico continua (auditoria,
          análises), sem o autor. Para só impedir o acesso, use <em>Bloquear</em>.
        </p>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} title={user.name} footer={<Button onClick={onClose}>Fechar</Button>}>
      <p className="-mt-2 text-sm text-muted">{user.email}</p>
      {error && <Alert kind="error">{error}</Alert>}
      {notice && <Alert kind="ok">{notice}</Alert>}

      <section className="space-y-2">
        <h3 className="text-sm font-medium">Nome</h3>
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} aria-label="Nome" />
          <Button variant="secondary" disabled={name === user.name || !name.trim()} loading={busy} onClick={() => act(() => api.patch(`/api/admin/users/${user.id}`, { name }), "Nome alterado.")}>
            Salvar
          </Button>
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-medium">Acessos</h3>
        <ul className="space-y-1">
          {user.memberships.map((m) => (
            <li key={m.id} className="flex items-center gap-2 text-sm">
              <Badge tone="brand">{ROLE_LABEL[m.role]}</Badge>
              <span className="text-muted">{scopeLabel(m, idx)}</span>
              <button
                type="button"
                className="ml-auto rounded p-1 text-muted hover:bg-surface-2 hover:text-danger"
                aria-label="Remover acesso"
                title="Remover acesso"
                onClick={() => act(() => api.del(`/api/admin/memberships/${m.id}`), "Acesso removido.")}
              >
                <Trash2 className="size-4" />
              </button>
            </li>
          ))}
        </ul>
        <div className="rounded-md border border-line p-3">
          <GrantPicker tree={tree} value={grant} onChange={setGrant} />
          <Button className="mt-3" size="sm" variant="secondary" loading={busy} onClick={() => act(() => api.post(`/api/admin/users/${user.id}/memberships`, grant), "Acesso concedido.")}>
            <Plus className="size-3.5" /> Conceder acesso
          </Button>
        </div>
      </section>

      <section className="flex flex-wrap gap-2 border-t border-line pt-4">
        {user.pendingInvite && (
          <Button size="sm" variant="secondary" onClick={() => act(() => api.post(`/api/admin/users/${user.id}/invite`), "Convite reenviado.")}>
            <Mail className="size-3.5" /> Reenviar convite
          </Button>
        )}
        {user.totpEnabled && (
          <Button size="sm" variant="secondary" onClick={() => act(() => api.post(`/api/admin/users/${user.id}/mfa-reset`), "2FA zerado. O usuário cadastra de novo no próximo login.")}>
            <ShieldOff className="size-3.5" /> Zerar 2FA
          </Button>
        )}
        <Button
          size="sm"
          variant="secondary"
          loading={busy}
          onClick={() =>
            run(async () => {
              const r = await api.post<{ temporaryPassword: string }>(`/api/admin/users/${user.id}/temporary-password`);
              setTemporary(r.temporaryPassword);
            })
          }
        >
          <KeyRound className="size-3.5" /> Senha temporária
        </Button>
        {user.disabled ? (
          <Button size="sm" variant="secondary" onClick={() => act(() => api.patch(`/api/admin/users/${user.id}`, { disabled: false }), "Usuário desbloqueado.")}>
            <UserCheck className="size-3.5" /> Desbloquear
          </Button>
        ) : (
          <Button size="sm" variant="secondary" onClick={() => act(() => api.patch(`/api/admin/users/${user.id}`, { disabled: true }), "Usuário bloqueado e sessões encerradas.")}>
            <UserX className="size-3.5" /> Bloquear
          </Button>
        )}
        <Button size="sm" variant="danger" className="ml-auto" onClick={() => setConfirmDelete(true)}>
          <Trash2 className="size-3.5" /> Apagar usuário
        </Button>
      </section>
      <p className="flex items-center gap-1 text-xs text-muted">
        <KeyRound className="size-3" aria-hidden /> A senha temporária é gerada pelo sistema e mostrada só uma vez; o usuário troca no primeiro
        acesso.
      </p>
    </Modal>
  );
}
